import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Loader2, Send, RefreshCw, Copy, X, CheckCircle2, XCircle,
  Calendar, DollarSign, Users, Percent, TrendingUp, ExternalLink,
} from "lucide-react";
import SourceBadge, { LEAD_SOURCES, LEAD_SOURCE_META } from "./SourceBadge";

const LEAD_STATUS_META = {
  new:       { label: "New",       classes: "bg-blue-100 text-blue-800 border-blue-200" },
  contacted: { label: "Contacted", classes: "bg-amber-100 text-amber-800 border-amber-200" },
  scheduled: { label: "Scheduled", classes: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  converted: { label: "Converted", classes: "bg-emerald-200 text-emerald-900 border-emerald-300" },
  lost:      { label: "Lost",      classes: "bg-rose-100 text-rose-800 border-rose-200" },
  spam:      { label: "Spam",      classes: "bg-gray-200 text-gray-700 border-gray-300" },
};

function LeadStatusPill({ status }) {
  const meta = LEAD_STATUS_META[status] || LEAD_STATUS_META.new;
  return (
    <span
      className={`inline-flex items-center text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border ${meta.classes}`}
      data-testid={`lead-status-${status}`}
    >
      {meta.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function LeadsPanel() {
  const [source, setSource] = useState("");   // "" = all
  const [status, setStatus] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null); // full lead detail
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (source) params.set("source", source);
      if (status) params.set("status", status);
      const [{ data: list }, { data: st }] = await Promise.all([
        api.get(`/admin/leads?${params.toString()}`),
        api.get(`/admin/lead-stats`),
      ]);
      setLeads(list.leads || []);
      setStats(st);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load leads");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source, status]);

  const openDetail = async (lead) => {
    setBusy(`open-${lead.lead_id}`);
    try {
      const { data } = await api.get(`/admin/leads/${lead.lead_id}`);
      setSelected(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load lead");
    } finally { setBusy(null); }
  };

  const sendWelcome = async (leadId) => {
    setBusy(`welcome-${leadId}`);
    try {
      const { data } = await api.post(`/admin/leads/${leadId}/send-welcome`);
      const parts = [];
      if (data?.sms?.ok) parts.push("SMS");
      if (data?.email?.ok) parts.push("email");
      toast.success(parts.length ? `Welcome sent · ${parts.join(" + ")}` : "Welcome queued");
      // Also copy the link so admin can paste elsewhere
      try { await navigator.clipboard.writeText(data.resume_link); } catch (_e) { /* noop */ }
      load();
      if (selected?.lead?.lead_id === leadId) openDetail(selected.lead);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(null); }
  };

  const setLeadStatus = async (leadId, newStatus) => {
    setBusy(`status-${leadId}`);
    try {
      await api.post(`/admin/leads/${leadId}/status`, { status: newStatus });
      toast.success(`Status → ${LEAD_STATUS_META[newStatus]?.label || newStatus}`);
      load();
      if (selected?.lead?.lead_id === leadId) openDetail(selected.lead);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
    } finally { setBusy(null); }
  };

  const bySource = stats?.by_source || [];
  const angiRow = useMemo(() => bySource.find((r) => r.source === "angi") ||
    { source: "angi", total_leads: 0, booked_leads: 0, completed_leads: 0, conversion_rate: 0, revenue: 0 }, [bySource]);

  return (
    <div className="space-y-5" data-testid="leads-panel">
      {/* ===== Stats cards ===== */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
        <StatCard label="Angi Leads"       value={angiRow.total_leads}     Icon={Users}      tone="orange" testid="stat-angi-total" />
        <StatCard label="Booked"           value={angiRow.booked_leads}    Icon={Calendar}   tone="emerald" testid="stat-angi-booked" />
        <StatCard label="Completed"        value={angiRow.completed_leads} Icon={CheckCircle2} tone="blue"  testid="stat-angi-completed" />
        <StatCard label="Conversion"       value={`${angiRow.conversion_rate}%`} Icon={Percent} tone="amber" testid="stat-angi-conversion" />
        <StatCard label="Revenue"          value={`$${(angiRow.revenue || 0).toFixed(0)}`} Icon={DollarSign} tone="emerald" testid="stat-angi-revenue" />
      </div>

      {/* Full source breakdown (compact table) */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4" data-testid="lead-source-breakdown">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5 text-emerald-700" /> Lead source breakdown
          </div>
          <button onClick={load} className="text-[11px] font-bold text-emerald-700 hover:underline flex items-center gap-1" data-testid="leads-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        {bySource.length === 0 ? (
          <div className="text-[12px] text-gray-500 italic">No leads recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-gray-500 font-extrabold">
                  <th className="pb-1.5">Source</th>
                  <th className="pb-1.5 text-right">Leads</th>
                  <th className="pb-1.5 text-right">Booked</th>
                  <th className="pb-1.5 text-right">Completed</th>
                  <th className="pb-1.5 text-right">Conv %</th>
                  <th className="pb-1.5 text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {bySource.map((r) => (
                  <tr key={r.source} className="border-t border-gray-100" data-testid={`stat-row-${r.source}`}>
                    <td className="py-1.5"><SourceBadge source={r.source} /></td>
                    <td className="py-1.5 text-right font-bold tabular-nums">{r.total_leads}</td>
                    <td className="py-1.5 text-right font-bold tabular-nums">{r.booked_leads}</td>
                    <td className="py-1.5 text-right font-bold tabular-nums">{r.completed_leads}</td>
                    <td className="py-1.5 text-right font-bold tabular-nums">{r.conversion_rate}%</td>
                    <td className="py-1.5 text-right font-bold tabular-nums">${(r.revenue || 0).toFixed(0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== Filters ===== */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500">Filter by source</div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip label="All" active={source === ""} onClick={() => setSource("")} testid="leads-source-all" />
          {LEAD_SOURCES.map((s) => (
            <FilterChip key={s} label={LEAD_SOURCE_META[s].label}
              active={source === s} onClick={() => setSource(s)}
              testid={`leads-source-${s}`} />
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mt-3">Filter by status</div>
        <div className="flex flex-wrap gap-1.5">
          <FilterChip label="All" active={status === ""} onClick={() => setStatus("")} testid="leads-status-all" />
          {Object.keys(LEAD_STATUS_META).map((s) => (
            <FilterChip key={s} label={LEAD_STATUS_META[s].label}
              active={status === s} onClick={() => setStatus(s)}
              testid={`leads-status-${s}`} />
          ))}
        </div>
      </div>

      {/* ===== List ===== */}
      <div className="space-y-2" data-testid="leads-list">
        {loading ? (
          <div className="text-center py-10 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading leads…</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-10 text-gray-500 italic">No leads match the current filters.</div>
        ) : leads.map((l) => (
          <div key={l.lead_id}
               className="bg-white rounded-2xl border border-gray-200 p-4 hover:border-emerald-300 transition cursor-pointer"
               data-testid={`lead-row-${l.lead_id}`}
               onClick={() => openDetail(l)}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <SourceBadge source={l.source} />
                  <LeadStatusPill status={l.status} />
                  {l.booking_status && (
                    <span className="text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                      Booking: {l.booking_status}
                    </span>
                  )}
                  {l.has_appointment && (
                    <span className="text-[9px] font-extrabold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                      ✓ Appointment
                    </span>
                  )}
                </div>
                <div className="mt-1 font-extrabold text-gray-900">
                  {l.name || "Unknown"} <span className="text-gray-500 font-normal">· {l.service_name}</span>
                </div>
                <div className="text-[12px] text-gray-600 truncate">{l.phone} {l.email && `· ${l.email}`}</div>
                {l.address && <div className="text-[11px] text-gray-500 truncate">{l.address}</div>}
                {l.notes && <div className="text-[11px] text-gray-500 mt-1 line-clamp-2">{l.notes}</div>}
                <div className="text-[10px] text-gray-400 mt-1">
                  {fmt(l.created_at)} {l.welcome_sent_at ? `· welcome sent ${fmt(l.welcome_sent_at)}` : ""}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 items-end shrink-0">
                <Button
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); sendWelcome(l.lead_id); }}
                  disabled={busy === `welcome-${l.lead_id}`}
                  data-testid={`lead-send-welcome-${l.lead_id}`}
                  className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold text-[11px] h-8 px-3"
                >
                  {busy === `welcome-${l.lead_id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                    <><Send className="w-3.5 h-3.5 mr-1" /> Send Welcome + Book Now</>}
                </Button>
                {l.booking_price != null && (
                  <div className="text-[10px] font-extrabold text-emerald-700">
                    ${Number(l.booking_price).toFixed(0)}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ===== Detail Drawer ===== */}
      {selected && (
        <LeadDetailDrawer
          selected={selected}
          onClose={() => setSelected(null)}
          onSendWelcome={() => sendWelcome(selected.lead.lead_id)}
          onSetStatus={(s) => setLeadStatus(selected.lead.lead_id, s)}
          busy={busy}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, Icon, tone, testid }) {
  const tones = {
    orange: "bg-orange-50 border-orange-200 text-orange-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
  };
  return (
    <div data-testid={testid} className={`rounded-2xl border-2 p-3 ${tones[tone] || tones.emerald}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.15em] font-extrabold">{label}</div>
        <Icon className="w-3.5 h-3.5 opacity-70" />
      </div>
      <div className="mt-1 text-xl sm:text-2xl font-extrabold tabular-nums tracking-tight">{value}</div>
    </div>
  );
}

function FilterChip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[11px] font-extrabold uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border transition ${
        active
          ? "bg-[#047857] text-white border-[#047857]"
          : "bg-white text-gray-700 border-gray-200 hover:border-emerald-300"
      }`}
    >
      {label}
    </button>
  );
}

function LeadDetailDrawer({ selected, onClose, onSendWelcome, onSetStatus, busy }) {
  const { lead, booking, customer } = selected;
  const raw = lead.raw_payload || {};
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { toast.error("Could not copy"); }
  };
  const resumeLink = lead.resume_token
    ? `${window.location.origin}/book?resume=${lead.resume_token}` : null;

  return (
    <div className="fixed inset-0 z-[80] bg-black/55 flex items-end sm:items-center justify-center p-3"
         onClick={onClose} data-testid="lead-detail-drawer">
      <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
           onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-[#062818] to-[#0B4029] text-white px-5 py-4 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-emerald-200 flex items-center gap-1.5">
              <SourceBadge source={lead.source} /> Lead detail
            </div>
            <div className="text-lg font-extrabold tracking-tight font-[Outfit] truncate">{lead.name || "Unknown"}</div>
            <div className="text-[11px] text-emerald-100/80 truncate">#{lead.lead_id.slice(-8)}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center" data-testid="lead-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 flex-1 space-y-4">
          {/* Actions row */}
          <div className="flex flex-wrap gap-2">
            <Button onClick={onSendWelcome} disabled={busy === `welcome-${lead.lead_id}`}
                    data-testid="lead-detail-send-welcome"
                    className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold">
              {busy === `welcome-${lead.lead_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> :
                <><Send className="w-4 h-4 mr-1.5" /> Send Welcome + Book Now</>}
            </Button>
            {resumeLink && (
              <Button variant="outline" onClick={() => copy(resumeLink)} data-testid="lead-detail-copy-link"
                      className="rounded-full">
                <Copy className="w-4 h-4 mr-1.5" /> Copy Booking Link
              </Button>
            )}
            {/* iter100 — Reply on Thumbtack (opens the pro portal thread). */}
            {lead.source === "thumbtack" && (lead.thumbtack_lead_id || lead.thumbtack_job_id) && (
              <a
                href={`https://pro.thumbtack.com/messages/${encodeURIComponent(lead.thumbtack_lead_id || lead.thumbtack_job_id)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lead-detail-reply-thumbtack"
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan-300 bg-cyan-50 px-4 py-2 text-[13px] font-extrabold text-cyan-800 hover:bg-cyan-100 transition"
              >
                <ExternalLink className="w-4 h-4" /> Reply on Thumbtack
              </a>
            )}
          </div>

          {/* Status controls */}
          <div className="rounded-2xl bg-gray-50 border border-gray-200 p-3">
            <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-1.5">Move lead to:</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.keys(LEAD_STATUS_META).map((s) => (
                <button key={s} onClick={() => onSetStatus(s)}
                        disabled={busy === `status-${lead.lead_id}` || lead.status === s}
                        data-testid={`lead-detail-status-${s}`}
                        className={`text-[11px] font-extrabold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full border transition ${
                          lead.status === s
                            ? `${LEAD_STATUS_META[s].classes} opacity-60 cursor-default`
                            : "bg-white text-gray-700 border-gray-200 hover:border-emerald-400"
                        }`}>
                  {LEAD_STATUS_META[s].label}
                </button>
              ))}
            </div>
          </div>

          {/* Customer + booking */}
          <div className="grid sm:grid-cols-2 gap-3">
            <InfoBlock title="Customer">
              <Row k="Name" v={lead.name} />
              <Row k="Email" v={lead.email} copy={copy} />
              <Row k="Phone" v={lead.phone} copy={copy} />
              <Row k="Address" v={lead.address} />
              {customer?.user_id && <Row k="Customer ID" v={customer.user_id} />}
            </InfoBlock>
            <InfoBlock title="Booking">
              <Row k="Status" v={booking?.status || "—"} />
              <Row k="Service" v={booking?.service_name || lead.service_name} />
              <Row k="Date" v={booking?.preferred_date || (lead.has_appointment ? lead.scheduled_date : "Pending customer")} />
              <Row k="Time window" v={booking?.preferred_time_window || lead.preferred_time_window || "—"} />
              <Row k="Price" v={booking?.price != null ? `$${Number(booking.price).toFixed(2)}` : "—"} />
              <Row k="Payment status" v={booking?.payment_status || "—"} />
            </InfoBlock>
          </div>

          {/* Welcome history */}
          {(lead.welcome_history || []).length > 0 && (
            <div className="rounded-2xl border border-gray-200 p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-1.5">
                Welcome history
              </div>
              <div className="space-y-1.5">
                {lead.welcome_history.slice().reverse().map((h, i) => (
                  <div key={i} className="text-[11px] text-gray-700 bg-gray-50 rounded-lg px-2 py-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold">{fmt(h.at)}</span>
                      <span className={`inline-flex items-center gap-0.5 ${h.sms?.ok ? "text-emerald-700" : "text-rose-700"}`}>
                        {h.sms?.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} SMS
                      </span>
                      <span className={`inline-flex items-center gap-0.5 ${h.email?.ok ? "text-emerald-700" : "text-rose-700"}`}>
                        {h.email?.ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} Email
                      </span>
                    </div>
                    {h.link && <div className="text-[10px] text-gray-500 truncate mt-0.5">{h.link}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Raw payload */}
          <details className="rounded-2xl border border-gray-200 p-3" data-testid="lead-raw-payload">
            <summary className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 cursor-pointer">
              Raw payload (as received from {lead.source})
            </summary>
            <pre className="mt-2 text-[11px] bg-gray-50 p-2 rounded-lg overflow-x-auto max-h-64">{JSON.stringify(raw, null, 2)}</pre>
          </details>
        </div>
      </div>
    </div>
  );
}

function InfoBlock({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-gray-500 mb-1.5">{title}</div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ k, v, copy }) {
  return (
    <div className="text-[12px] flex items-start gap-2">
      <div className="text-gray-500 min-w-[80px]">{k}</div>
      <div className="font-bold text-gray-900 truncate flex-1">{v || "—"}</div>
      {copy && v && <button onClick={() => copy(v)} className="text-gray-400 hover:text-emerald-700"><Copy className="w-3 h-3" /></button>}
    </div>
  );
}
